import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Select,
  MenuItem,
  Button,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { createAffiliate, fetchAffiliates, fetchReferalManagerAffiliates, updateAffiliate } from '../services/api';
import EditIcon from '@mui/icons-material/Edit';

export default function AffiliateForm() {
  // Add table state
  const [affiliates, setAffiliates] = useState([]);
  const [tableLoading, setTableLoading] = useState(true);
  const [setSnackbar] = useState({ 
    open: false, 
    message: '', 
    severity: 'success' 
  });
  const { hasRole, fetchPartnerName } = useAuth();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    partner_name: '',
    status: 'ACTIVE'
  });
  const [loading, setLoading] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editData, setEditData] = useState({
    id: '',
    name: '',
    email: '',
    status: 'ACTIVE'
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleEditChange = (field, value) => {
    setEditData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await createAffiliate(formData);
      let affiliateData = [];
      if (hasRole('admin')) {
        affiliateData = await fetchAffiliates();
      } else {
        affiliateData = await fetchReferalManagerAffiliates();
      }
      
      setAffiliates(affiliateData);
      setSnackbar({
        open: true,
        message: 'Affiliate created successfully',
        severity: 'success'
      });
      setFormData({ name: '', email: '', partner_name: '', comission_rate: 0, status: 'ACTIVE' });
    } catch (err) {
      setSnackbar({
        open: true,
        message: err || 'Failed to create affiliate',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEditSubmit = async () => {
    setLoading(true);
    try {
      await updateAffiliate(editData.id, {
        name: editData.name,
        email: editData.email,
        status: editData.status,
        partner_name: editData.partner_name,
        commission_rate: editData.commission_rate
      });
      setLoading(false);
      
      let affiliateData = [];
      if (hasRole('admin')) {
        affiliateData = await fetchAffiliates();
      } else {
        affiliateData = await fetchReferalManagerAffiliates(fetchPartnerName());
      }
      
      setAffiliates(affiliateData);
      setEditDialogOpen(false);
      // setSnackbar({
      //   open: true,
      //   message: 'Affiliate updated successfully',
      //   severity: 'success'
      // });
    } catch (err) {
      setSnackbar({
        open: true,
        message: err || 'Failed to update affiliate',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (affiliate) => {
    setEditData({
      id: affiliate.id,
      name: affiliate.name,
      email: affiliate.email,
      status: affiliate.status,
      partner_name: affiliate.partner_name,
      commission_rate: affiliate.commission_rate
    });
    setEditDialogOpen(true);
  };

  useEffect(() => {
    const loadAffiliates = async () => {
      setTableLoading(true);
      try {
      
        let affiliateData = [];
        if (hasRole('admin')) {
          affiliateData = await fetchAffiliates();
        } else {
          affiliateData = await fetchReferalManagerAffiliates(fetchPartnerName());
        }
        setAffiliates(affiliateData);
      } catch (error) {
        console.error('Error loading affiliates:', error);
        setSnackbar({
          open: true,
          message: 'Failed to load affiliates',
          severity: 'error'
        });
      } finally {
        setTableLoading(false);
      }
    };
    loadAffiliates();
  }, [setSnackbar, fetchPartnerName, hasRole]);

  return (

    <Box sx={{ width: '100%', p: 2 }}>
      <Typography variant="h5" sx={{ mb: 3 }}>
        Create New Affiliate
      </Typography>
      
      <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
      <form onSubmit={handleSubmit}>
        <TextField
          label="Full Name"
          variant="outlined"
          fullWidth
          margin="normal"
          value={formData.name}
          onChange={(e) => setFormData({...formData, name: e.target.value})}
          required
        />
        <TextField
          label="Email"
          variant="outlined"
          type="email"
          fullWidth
          margin="normal"
          value={formData.email}
          onChange={(e) => setFormData({...formData, email: e.target.value})}
          required
        />
        <TextField
          label="Partner Name"
          variant="outlined"
          fullWidth
          margin="normal"
          value={formData.partner_name}
          onChange={(e) => setFormData({...formData, partner_name: e.target.value})}
          required
        />
        <TextField
          label="Commission Rate"
          name="commission_rate"
          type="number"
          required
          fullWidth
          onChange={(e) => handleChange('commission_rate', parseInt(e.target.value))}
          value={formData.commission_rate}
        />
        <FormControl fullWidth margin="normal">
          <InputLabel>Status</InputLabel>
          <Select
            value={formData.status}
            label="Status"
            onChange={(e) => setFormData({...formData, status: e.target.value})}
          >
            <MenuItem value="ACTIVE">Active</MenuItem>
            <MenuItem value="INACTIVE">Inactive</MenuItem>
          </Select>
        </FormControl>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          disabled={loading}
          sx={{ mt: 2 }}
        >
          {loading ? <CircularProgress size={24} /> : 'Create Affiliate'}
        </Button>
      </form>

        <Typography variant="h6" component="h2" gutterBottom sx={{ mt: 3, mb: 2 }}>
          Existing Affiliates
        </Typography>
        
        {tableLoading ? (
          <CircularProgress />
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Partner</TableCell>
                  <TableCell>Rate</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {affiliates.map((affiliate) => (
                  <TableRow key={affiliate.id}>
                    <TableCell>{affiliate.name}</TableCell>
                    <TableCell>{affiliate.email}</TableCell>
                    <TableCell>{affiliate.partner_name}</TableCell>
                    <TableCell>{affiliate.commission_rate}</TableCell>
                    <TableCell>
                      <Chip 
                        label={affiliate.status} 
                        color={affiliate.status === 'ACTIVE' ? 'success' : 'error'}
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton 
                        color="primary" 
                        onClick={() => handleEditClick(affiliate)}
                        size="small"
                      >
                        <EditIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Affiliate</DialogTitle>
        <DialogContent>
          <TextField
            label="Full Name"
            variant="outlined"
            fullWidth
            margin="normal"
            value={editData.name}
            onChange={(e) => handleEditChange('name', e.target.value)}
            required
          />
          <TextField
            label="Email"
            variant="outlined"
            type="email"
            fullWidth
            margin="normal"
            value={editData.email}
            onChange={(e) => handleEditChange('email', e.target.value)}
            required
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Status</InputLabel>
            <Select
              value={editData.status}
              label="Status"
              onChange={(e) => handleEditChange('status', e.target.value)}
            >
              <MenuItem value="ACTIVE">Active</MenuItem>
              <MenuItem value="INACTIVE">Inactive</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleEditSubmit} 
            variant="contained" 
            color="primary"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
      </Box>
  );
}